import type { NetworkFields } from '@tribeplatform/gql-client/graphql'
import type {
  Network,
  MutationAddMemberSchemaFieldArgs,
  AuthToken,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getAuthTokenKey } from '../../utils/keys/authToken.keys.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'

export const useAddMemberSchemaField = (options?: {
  fields?: NetworkFields
  useMutationOptions?: UseMutationOptions<
    Network,
    Error,
    MutationAddMemberSchemaFieldArgs
  >
}) => {
  const { useMutationOptions, fields = 'default' } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const networkKey = getNetworkKey()
  const authTokenKey = getAuthTokenKey()

  return useMutation<Network, Error, MutationAddMemberSchemaFieldArgs>(
    input => client.network.addMemberSchemaField(input, fields),
    {
      onMutate: variables => {
        queryClient.cancelQueries(networkKey)
        queryClient.cancelQueries(authTokenKey)

        queryClient.setQueriesData<AuthToken>(authTokenKey, old => ({
          ...old,
          network: {
            ...old?.network,
            memberFields: {
              fields: [
                ...(old?.network?.memberFields?.fields ?? []),
                variables?.input,
              ],
            },
          },
        }))
        queryClient.setQueriesData<Network>(networkKey, old => ({
          ...old,
          memberFields: {
            fields: [...(old?.memberFields?.fields ?? []), variables?.input],
          },
        }))
      },
      onSettled: () => {
        queryClient.invalidateQueries(networkKey)
        queryClient.invalidateQueries(authTokenKey)
      },
      ...useMutationOptions,
    },
  )
}
